import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Space,
  Typography,
  Row,
  Col,
  Avatar,
  Divider,
  Button,
  Tag,
  Input,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Severty, ShowToast } from "../helper/toast";

const { Title, Text } = Typography;

const PERMISSION_CONFIG = [
  { key: "can_view", label: "View", icon: <EyeOutlined /> },
  { key: "can_create", label: "Create", icon: <PlusOutlined /> },
  { key: "can_edit", label: "Edit", icon: <EditOutlined /> },
  { key: "can_delete", label: "Delete", icon: <DeleteOutlined /> },
  { key: "can_maker", label: "Reviewer", icon: <EditOutlined /> },
  { key: "can_checker", label: "Authoriser", icon: <CheckOutlined /> },
];

const RolePermissionsEditModal = ({
  open,
  role,
  modules,
  onClose,
  onSubmit,
  onDelete,
  loading,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [assigned_users_count, setUsersCount] = useState("");
  /* ---------- INIT STATE ---------- */
  useEffect(() => {
    if (!open) return;

    setName(role?.name || "");
    setDescription(role?.description || "");
    setUsersCount(role?.assigned_users_count || 0);

    const rolePermMap = {};
    role?.module_permissions?.forEach((p) => {
      const slug = typeof p.module === "string" ? p.module : p.module?.slug;
      if (slug) {
        rolePermMap[slug] = p;
      }
    });
    // console.log("assigned_users_count==>", role.assigned_users_count)

    setPermissions(
      modules.map((m) => {
        const rp = rolePermMap[m.slug] || {};
        return {
          module_slug: m.slug,
          module_name: m.name,
          can_view: !!rp.can_view,
          can_create: !!rp.can_create,
          can_edit: !!rp.can_edit,
          can_delete: !!rp.can_delete,
          can_maker: !!rp.can_maker,
          can_checker: !!rp.can_checker,
        };
      }),
    );
  }, [open, role, modules]);

  /* ---------- TOGGLE ---------- */
  const togglePermission = (slug, key) => {
    setPermissions((prev) =>
      prev.map((p) => (p.module_slug === slug ? { ...p, [key]: !p[key] } : p)),
    );
  };

  /* ---------- PERMISSION PILL ---------- */
  const PermissionTag = ({ active, label, icon, onClick }) => (
    <Tag
      onClick={onClick}
      icon={active ? <CheckOutlined /> : <CloseOutlined />}
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 12,
        border: "none",
        fontWeight: 600,
        background: active
          ? "linear-gradient(135deg, #22c55e, #16a34a)"
          : "#f1f5f9",
        color: active ? "#fff" : "#374151",
      }}
    >
      {icon} {label}
    </Tag>
  );

  /* ---------- SUBMIT ---------- */
  const handleSave = () => {
    if (loading) return;
    try {
      onSubmit({
        name,
        description,
        modules: permissions,
      });

    } catch (error) {
      ShowToast(error?.response?.data?.message || "Failed to save role", Severty.ERROR);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      width={920}
      centered
      destroyOnClose
      styles={{
        content: {
          borderRadius: 16,
          overflow: "hidden",
        },
      }}
      footer={[
        <div className="d-flex justify-content-end gap-2">
          <Tooltip
            title={
              assigned_users_count > 0
                ? "You cannot delete this role because users are assigned to it."
                : ""
            }
          >
            <span>
              <Popconfirm
                title="Delete this role?"
                description="This action cannot be undone."
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={assigned_users_count > 0}
                onConfirm={onDelete}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={assigned_users_count > 0}
                >
                  Delete Role
                </Button>
              </Popconfirm>
            </span>
          </Tooltip>
          <Button key="cancel" size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button key="save" type="primary" size="large" onClick={handleSave} loading={loading} disabled={loading}   >
            {role ? "Update Role" : "Create Role"}
          </Button>
        </div>
      ]}
    >
      {/* ===== HEADER ===== */}
      <div
        style={{
          padding: "18px 20px",
          background: role
            ? "linear-gradient(135deg, #e0f2fe, #bae6fd)"
            : "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Space align="center" size={16} style={{ marginBottom: 16 }}>
          <Avatar
            size={44}
            style={{
              background: role
                ? "linear-gradient(135deg, #2563eb, #3b82f6)"
                : "linear-gradient(135deg, #16a34a, #22c55e)",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {role ? "✏️" : "➕"}
          </Avatar>

          <div>
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              <Space align="center">
                <Title level={4} style={{ margin: 0, fontSize: 26 }}>
                  {role ? "Edit Role" : "Create New Role"}
                </Title>

                {assigned_users_count > 0 && <Tag color="gold">In Use</Tag>}
              </Space>
              <Text type="secondary">
                {role
                  ? "Modify role details and permissions"
                  : "Define a role and assign permissions"}
              </Text>
            </Space>
          </div>
        </Space>

        <Row gutter={[16, 16]}>
          <Col span={24} md={12}>
            <Input
              size="large"
              placeholder="Role name"
              value={name}
              className="custom-input12"
              onChange={(e) => setName(e.target.value)}
            />
          </Col>

          <Col span={24} md={12}>
            <Input
              size="large"
              placeholder="Short description (optional)"
              value={description}
              className="custom-input12"

              onChange={(e) => setDescription(e.target.value)}
            />
          </Col>
        </Row>
      </div>

      <Divider style={{ margin: 0 }} />

      {/* ===== MODULES ===== */}
      <div style={{ padding: "14px", maxHeight: "65vh", overflowY: "auto" }}>
        <Row gutter={[14, 14]}>
          {permissions.map((mod) => (
            <Col span={24} md={12} key={mod.module_slug}>
              <Card
                hoverable
                style={{
                  borderRadius: 16,
                  border: "1px solid #dbe4ee",
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div
                  style={{
                    padding: "14px 16px",
                    background: "linear-gradient(135deg, #f8fbff, #eef4ff)",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <Space align="center" style={{ marginBottom: 2 }}>
                    <Avatar
                      style={{
                        background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                        fontWeight: 700,
                      }}
                    >
                      {mod.module_name[0]}
                    </Avatar>

                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {mod.module_name}
                      </Title>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Module permissions
                      </Text>
                    </div>
                  </Space>
                </div>

                <div style={{ padding: 14 }}>
                  <Space wrap size={[8, 8]}>
                    {PERMISSION_CONFIG.map(({ key, label, icon }) => {
                      if (
                        (key === "can_maker" || key === "can_checker") &&
                        mod.module_slug !== "kyc_kyb"
                      ) {
                        return null;
                      }
                      return (
                        <PermissionTag
                          key={key}
                          label={label}
                          icon={key === "can_maker" || key === "can_checker" ? null : icon}
                          active={mod[key]}
                          onClick={() => togglePermission(mod.module_slug, key)}
                        />
                      );
                    })}
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </Modal>
  );
};

export default RolePermissionsEditModal;
